import { useEffect, useCallback, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from 'assets/images/shopping/closeIcon';
import CopyIcon from 'assets/images/shopping/CopyIcon';
import BordFlag from 'components/NudosComponents/BordFlag/BordFlag';
import { TcountryCode } from 'components/NudosComponents/BordFlag/BordFlag.types';
import GenericLoader from 'components/GenericLoader/GenericLoader';
import useLockScroll from 'hooks/useLockScroll';
import { IProductDelivery, IproductReferenceDataInCountry, IShipmentData } from 'types/orders';
import { getOrderDetailedData } from 'services/orders';
import { copyTextToClipboard, getDestinationDisplayName, groupProductsBySku } from '../../trackingOrder.utils';
import SerialsModal from '../../../orders/Components/SerialsModal/SerialsModal';
import ShipmentProductRow from './ShipmentProductRow';
import './ShipmentDetailDrawer.scss';

interface IShipmentDetailDrawer {
  isOpen: boolean;
  onClose: () => void;
  orderId?: number | string;
  shipmentId?: number;
}

/** ID del contenedor donde se renderizará el drawer mediante Portal */
const PORTAL_CONTAINER_ID = 'ordersListingOperations';

/**
 * Modal drawer que se desliza desde la derecha mostrando el detalle
 * de un envío incluyendo información del receptor y lista de productos.
 * Consulta el endpoint de detalle de orden para obtener información completa del envío.
 */
const ShipmentDetailDrawer = ({
  isOpen,
  onClose,
  orderId,
  shipmentId,
}: IShipmentDetailDrawer) => {
  const [shipmentData, setShipmentData] = useState<IShipmentData | null>(null);
  const [references, setReferences] = useState<IproductReferenceDataInCountry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [selectedProductForSerial, setSelectedProductForSerial] = useState<IProductDelivery | null>(null);
  const [productsForSerialModal, setProductsForSerialModal] = useState<IProductDelivery[]>([]);

  const destinationTitle = getDestinationDisplayName(
    shipmentData?.place,
    shipmentData?.receiverName || shipmentData?.receiverInfo?.receiverName,
    shipmentData?.country?.name
  );

  const countryCode = shipmentData?.country?.code?.toLowerCase() as TcountryCode;
  const products = shipmentData?.products || [];
  const receiverInfo = shipmentData?.receiverInfo;

  /**
   * Obtiene los detalles del envío desde el endpoint de detalle de orden.
   */
  const fetchShipmentDetail = useCallback(async () => {
    if (!orderId || !shipmentId) return;

    setIsLoading(true);
    setHasError(false);

    try {
      const orderData = await getOrderDetailedData(orderId);
      const shipments = orderData?.generalData?.shipmentData || [];
      const foundShipment = shipments.find(s => s.shipmentId === shipmentId);

      // Extraer todas las referencias de listProducts
      const allReferences = Object.values(orderData?.listProducts || {}).flatMap(
        country => country?.references || []
      );
      setReferences(allReferences);

      if (foundShipment) {
        setShipmentData(foundShipment);
      } else {
        setHasError(true);
        setShipmentData(null);
      }
    } catch {
      setHasError(true);
      setShipmentData(null);
    } finally {
      setIsLoading(false);
    }
  }, [orderId, shipmentId]);

  /**
   * Maneja el click en el overlay, cerrando el drawer solo si el modal de seriales no está abierto.
   */
  const handleOverlayClick = () => {
    if (!selectedProductForSerial) {
      onClose();
    }
  };

  /**
   * Abre el modal de seriales para un producto específico.
   * Agrupa los productos por SKU y pasa solo los del mismo tipo.
   * @param product - Producto seleccionado
   */
  const handleOpenSerialsModal = (product: IProductDelivery) => {
    const groupedProducts = groupProductsBySku(products);
    const productSku = product?.sku || 'sin-sku';
    const sameSkuProducts = groupedProducts[productSku] || [product];

    setSelectedProductForSerial(product);
    setProductsForSerialModal(sameSkuProducts);
  };

  const handleCloseSerialsModal = () => {
    setSelectedProductForSerial(null);
    setProductsForSerialModal([]);
  };

  /**
   * Callback después de actualizar los seriales exitosamente.
   */
  const handleSerialsUpdated = () => {
    fetchShipmentDetail();
  };

  useEffect(() => {
    if (isOpen && orderId && shipmentId) {
      fetchShipmentDetail();
    }
  }, [isOpen, orderId, shipmentId, fetchShipmentDetail]);

  const additionalElementsToLock = useMemo(() => [PORTAL_CONTAINER_ID], []);
  useLockScroll(isOpen, additionalElementsToLock);

  const resetShipmentState = () => {
    setShipmentData(null);
    setReferences([]);
    setHasError(false);
    setSelectedProductForSerial(null);
    setProductsForSerialModal([]);
  };
  
  useEffect(() => {
    if (!isOpen) {
      resetShipmentState();
    }
  }, [isOpen]);

  const portalContainer = document.getElementById(PORTAL_CONTAINER_ID);

  if (!isOpen) return null;

  const drawerContent = (
    <>
      <div className="shipmentDetailDrawerOverlay" onClick={handleOverlayClick} />

      <div className="shipmentDetailDrawer">
        <header className="drawerHeader">
          <h2 className="drawerTitle">Detalle del envío</h2>
          <button className="closeButton" onClick={onClose} aria-label="Cerrar modal">
            <CloseIcon />
          </button>
        </header>

        <div className="drawerContent">
          {isLoading && (
            <div className="loadingState">
              <GenericLoader sizeLoader={60} />
            </div>
          )}

          {hasError && !isLoading && (
            <div className="errorState">
              <span>Error al cargar el detalle del envío</span>
              <button className="retryButton" onClick={fetchShipmentDetail}>
                Reintentar
              </button>
            </div>
          )}

          {!isLoading && !hasError && shipmentData && (
            <>
              <div className="destinationHeader">
                <span className="destinationTitle">
                  Envío {destinationTitle}
                  <button
                    className="copyButton"
                    onClick={() => copyTextToClipboard(destinationTitle)}
                    aria-label="Copiar destino"
                  >
                    <CopyIcon />
                  </button>
                </span>
              </div>

              <div className="receiverInfoGrid">
                <div className="infoField">
                  <span className="fieldLabel">Documento de identidad</span>
                  <span className="fieldValue">
                    {receiverInfo?.receiverIdDocument || shipmentData?.receiverIdDocument || 'Sin información'}
                  </span>
                </div>

                <div className="infoField">
                  <span className="fieldLabel">Número de teléfono</span>
                  <span className="fieldValue">
                    {receiverInfo?.receiverPhoneCountryCode && receiverInfo?.receiverPhone
                      ? `${receiverInfo?.receiverPhoneCountryCode} ${receiverInfo?.receiverPhone}`
                      : shipmentData?.receiverPhone || 'Sin información'}
                  </span>
                </div>

                <div className="infoField">
                  <span className="fieldLabel">País</span>
                  <span className="fieldValue countryValue">
                    {shipmentData.country?.name || 'Sin información'}
                    {countryCode && <BordFlag country={countryCode} variant="square" customWidth="16px" />}
                  </span>
                </div>

                <div className="infoField">
                  <span className="fieldLabel">Ciudad</span>
                  <span className="fieldValue">{shipmentData?.city || 'Sin información'}</span>
                </div>

                <div className="infoField">
                  <span className="fieldLabel">Código Postal</span>
                  <span className="fieldValue">{shipmentData?.zipCode || 'Sin información'}</span>
                </div>
              </div>

              <div className="addressSection">
                <div className="infoField">
                  <span className="fieldLabel">Dirección</span>
                  <span className="fieldValue">
                    {shipmentData?.destinationAddress || shipmentData?.deliveryAddress || 'Sin información'}
                  </span>
                </div>

                <div className="infoField">
                  <span className="fieldLabel">Indicaciones</span>
                  <span className="fieldValue">{shipmentData?.additionalReferences || 'Sin indicaciones'}</span>
                </div>
              </div>

              <div className="productsSection">
                {products?.length ? (
                  products?.map((product, index) => (
                    <ShipmentProductRow
                      key={`product-${product.id}-${index}`}
                      product={product}
                      productNumber={index + 1}
                      totalProducts={products?.length}
                      status={shipmentData?.status}
                      onSerialClick={() => handleOpenSerialsModal(product)}
                    />
                  ))
                ) : (
                  <div className="noProducts">No hay productos en este envío</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {selectedProductForSerial && (
        <SerialsModal
          singleProduct={selectedProductForSerial}
          products={productsForSerialModal}
          productInfo={{
            name: selectedProductForSerial.name,
            pills: selectedProductForSerial.pills,
            units: productsForSerialModal.length
          }}
          references={references}
          changeShowModal={handleCloseSerialsModal}
          updateWithoutLoaders={handleSerialsUpdated}
        />
      )}
    </>
  );

  // Usa portal si el contenedor existe, sino renderiza normalmente
  if (portalContainer) {
    return createPortal(drawerContent, portalContainer);
  }

  return drawerContent;
};

export default ShipmentDetailDrawer;
